import React from 'react';
import { useNavigate } from 'react-router-dom';
import MinimalLayout from '../../components/minimal/MinimalLayout';
import TodayCard from '../../components/minimal/TodayCard';
import IntelCard from '../../components/minimal/IntelCard';
import { Sparkles } from 'lucide-react';

const HomeMinimal = () => {
  const navigate = useNavigate();
  const userName = localStorage.getItem('userName') || 'Friend';

  // Determine time of day
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
  const timeEmoji = hour < 6 ? '🌙' : hour < 12 ? '☀️' : hour < 17 ? '🌤️' : hour < 21 ? '🌅' : '🌙';

  const domains = [
    { id: 'health', name: 'Health', icon: '❤️', score: '8.3', path: '/health-minimal' },
    { id: 'career', name: 'Career', icon: '💼', score: '6.5', path: '/domain/career' },
    { id: 'relationships', name: 'Love', icon: '💕', score: '7.2', path: '/domain/relationships' },
    { id: 'wealth', name: 'Wealth', icon: '💰', score: '5.8', path: '/domain/wealth' },
    { id: 'family', name: 'Family', icon: '👨‍👩‍👧‍👦', score: '7.8', path: '/domain/family' },
    { id: 'education', name: 'Education', icon: '📚', score: '7.0', path: '/domain/education' },
  ];

  const quickInsights = [
    {
      title: 'Energy Peak',
      subtitle: '4:00 PM - 6:00 PM today',
      icon: '💪',
      value: 'High',
    },
    {
      title: 'Mental Clarity',
      subtitle: 'Mercury well-placed',
      icon: '🧠',
      value: 'Good',
    },
    {
      title: 'Caution Window',
      subtitle: 'Avoid conflicts 8-10 PM',
      icon: '⚠️',
      value: 'Alert',
    },
  ];

  return (
    <MinimalLayout
      title={`${timeEmoji} Good ${greeting}, ${userName}`}
      showNotifications={true}
      notificationCount={3}
      storyContent={
        <div className="space-y-4">
          <div className="aspect-video bg-gradient-to-br from-cosmic-500 to-sacred-500 rounded-lg flex items-center justify-center text-white">
            <div className="text-center">
              <div className="text-4xl mb-2">🎬</div>
              <p className="text-sm font-medium">Your Life Story</p>
              <p className="text-xs opacity-75 mt-1">Watch your personalized video</p>
            </div>
          </div>
        </div>
      }
      dashboardContent={
        <div className="space-y-4">
          <h3 className="font-serif text-xl font-medium">Life Overview</h3>
          <div className="grid grid-cols-2 gap-3">
            {domains.map((domain) => (
              <div key={domain.id} className="p-4 bg-stone-50 rounded-lg text-center">
                <div className="text-2xl mb-1">{domain.icon}</div>
                <div className="text-xs text-stone-600 mb-1">{domain.name}</div>
                <div className="text-lg font-bold text-stone-900">{domain.score}</div>
              </div>
            ))}
          </div>
        </div>
      }
      timelineContent={
        <div className="space-y-4">
          <h3 className="font-serif text-xl font-medium">Your Life Timeline</h3>
          <div className="space-y-3">
            <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg">
              <div className="text-xs text-stone-600 mb-1">Present (2024)</div>
              <div className="font-medium text-stone-900">Growth & Expansion Phase</div>
            </div>
            <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg">
              <div className="text-xs text-stone-600 mb-1">2025-2027</div>
              <div className="font-medium text-stone-900">Career Transformation</div>
            </div>
            <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg">
              <div className="text-xs text-stone-600 mb-1">2028-2030</div>
              <div className="font-medium text-stone-900">Prosperity Peak</div>
            </div>
          </div>
        </div>
      }
    >
      {/* Today's Cosmic Summary */}
      <TodayCard
        summary="Your cosmic energy is balanced today with minor challenges"
        score="7.4"
        icon="✨"
        gradient="from-cosmic-100 to-sacred-100"
      />

      {/* AI Agents Button */}
      <button
        onClick={() => navigate('/ai-agents')}
        className="w-full bg-gradient-to-r from-cosmic-500 via-sacred-500 to-cosmic-500 hover:from-cosmic-600 hover:to-sacred-600 text-white p-4 rounded-lg shadow-lg hover:shadow-xl transition-all"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="text-2xl">🤖</div>
            <div className="text-left">
              <div className="font-medium">Your 8 AI Agents</div>
              <div className="text-xs opacity-90">Get personalized guidance</div>
            </div>
          </div>
          <Sparkles className="w-5 h-5" />
        </div>
      </button>

      {/* Quick Insights */}
      <div>
        <h2 className="text-sm font-medium text-stone-900 mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-sacred-500" />
          Today's Insights
        </h2>
        <div className="space-y-2">
          {quickInsights.map((insight, index) => (
            <IntelCard
              key={index}
              title={insight.title}
              subtitle={insight.subtitle}
              icon={insight.icon}
              value={insight.value}
            />
          ))}
        </div>
      </div>

      {/* Life Domains */}
      <div>
        <h2 className="text-sm font-medium text-stone-900 mb-3">Your Life Domains</h2>
        <div className="space-y-2">
          {domains.map((domain) => (
            <IntelCard
              key={domain.id}
              title={domain.name}
              subtitle="View detailed intelligence"
              icon={domain.icon}
              value={domain.score}
              onClick={() => navigate(domain.path)}
            />
          ))}
        </div>
      </div>
    </MinimalLayout>
  );
};

export default HomeMinimal;
